import { create } from 'zustand';
import {
  DENSITY_KEY,
  THEME_STORAGE_KEY,
  createUserPreferenceStore,
} from '../lib/preferences';
import { useSession } from './session';

// Interface state: theme, table density, and which global overlay is open.
// Client state only (CLAUDE.md §11) — nothing here mirrors server data.

export type Theme = 'light' | 'dark';
export type Density = 'comfortable' | 'compact';

/** Both classes ship the same token names, so a component reading only `var()` is
 *  correct in either without a second stylesheet. */
const THEME_CLASS: Readonly<Record<Theme, string>> = {
  light: 'flock-light',
  dark: 'flock-dark',
};

/** What the boot script in index.html already stamped on <html>. Reading it back
 *  rather than recomputing keeps the two in step with one source of truth. */
function themeFromDocument(): Theme {
  return document.documentElement.classList.contains(THEME_CLASS.dark) ? 'dark' : 'light';
}

function applyTheme(theme: Theme): void {
  document.documentElement.className = THEME_CLASS[theme];
  try {
    window.localStorage.setItem(THEME_STORAGE_KEY, theme);
  } catch {
    // Storage denied. The theme still applies for this session.
  }
}

function preferences() {
  return createUserPreferenceStore(useSession.getState().session?.id ?? 'anonymous');
}

interface UiState {
  readonly theme: Theme;
  readonly density: Density;
  readonly isPaletteOpen: boolean;
  readonly isShortcutSheetOpen: boolean;
  toggleTheme(): void;
  setDensity(density: Density): void;
  openPalette(): void;
  closePalette(): void;
  toggleShortcutSheet(): void;
  closeShortcutSheet(): void;
  /** After sign-in: the person's own density, which may have come from another device (S32). */
  loadPreferences(): void;
}

export const useUi = create<UiState>((set, get) => ({
  theme: themeFromDocument(),
  density: preferences().get<Density>(DENSITY_KEY, 'comfortable'),
  isPaletteOpen: false,
  isShortcutSheetOpen: false,

  toggleTheme: () => {
    const next: Theme = get().theme === 'dark' ? 'light' : 'dark';
    applyTheme(next);
    set({ theme: next });
  },

  setDensity: (density) => {
    // Density is one setting for the whole app, not per screen (06-qol §3).
    preferences().set(DENSITY_KEY, density);
    set({ density });
  },

  // The palette and the shortcut sheet are mutually exclusive: two stacked
  // overlays would leave Esc ambiguous and trap focus between them.
  openPalette: () => set({ isPaletteOpen: true, isShortcutSheetOpen: false }),
  closePalette: () => set({ isPaletteOpen: false }),
  toggleShortcutSheet: () =>
    set((state) => ({ isShortcutSheetOpen: !state.isShortcutSheetOpen, isPaletteOpen: false })),
  closeShortcutSheet: () => set({ isShortcutSheetOpen: false }),
  loadPreferences: () => set({ density: preferences().get<Density>(DENSITY_KEY, 'comfortable') }),
}));
